"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import clsx from "clsx";
import { getCountries, getCountryCallingCode } from "libphonenumber-js/max";

import { useTranslation } from "~/app/hooks/useTranslation";

import styles from "./PhoneInputButton.module.scss";

const FALLBACK_LOCALE = "en";

function getDisplayNames(locale) {
  if (typeof Intl === "undefined" || typeof Intl.DisplayNames === "undefined") return null;
  try {
    return new Intl.DisplayNames([locale || FALLBACK_LOCALE], { type: "region" });
  } catch {
    return null;
  }
}

function buildCountryOptions(locale) {
  const displayLocale = locale || FALLBACK_LOCALE;
  const displayNames = getDisplayNames(displayLocale);
  const collator =
    typeof Intl !== "undefined" && Intl.Collator ? new Intl.Collator([displayLocale], { sensitivity: "base" }) : null;

  const options = getCountries().map((code) => ({
    code,
    name: displayNames?.of(code) || code,
    dialCode: getCountryCallingCode(code),
  }));

  if (collator) {
    options.sort((a, b) => collator.compare(a.name, b.name));
  } else {
    options.sort((a, b) => a.name.localeCompare(b.name));
  }

  return options;
}

function filterCountries(countries, searchValue) {
  if (!searchValue) return countries;
  const rawQuery = searchValue.trim();
  if (!rawQuery) return countries;
  const query = rawQuery.toLowerCase();
  const numericQuery = rawQuery.startsWith("+") ? rawQuery.slice(1) : rawQuery;
  const isNumericQuery = /^\d+$/.test(numericQuery);
  const isIsoQuery = /^[a-zA-Z]{2}$/.test(rawQuery);

  const matches = [];
  countries.forEach((item, index) => {
    const isoCode = item.code.toLowerCase();
    const name = item.name.toLowerCase();
    const codeExact = isIsoQuery && isoCode === query;
    const dialExact = isNumericQuery && item.dialCode === numericQuery;
    const codeMatch = isoCode.includes(query);
    const dialMatch = isNumericQuery && item.dialCode.includes(numericQuery);
    const nameMatch = name.includes(query);
    const isMatch = codeExact || dialExact || codeMatch || dialMatch || nameMatch;
    if (!isMatch) return;
    const score = codeExact || dialExact ? 0 : codeMatch ? 1 : dialMatch ? 2 : nameMatch ? 3 : 4;
    matches.push({ item, score, index });
  });

  matches.sort((a, b) => a.score - b.score || a.index - b.index);
  return matches.map(({ item }) => item);
}

export default function PhoneCountrySelect({
  locale,
  country,
  onCountryChange,
  disabled,
  searchPlaceholder,
  emptyLabel,
  compact = false,
  className,
  style,
}) {
  const { t } = useTranslation();
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const resolvedSearchPlaceholder = searchPlaceholder ?? t("common.phoneCountrySelect.searchPlaceholder");
  const resolvedEmptyLabel = emptyLabel ?? t("common.phoneCountrySelect.emptyLabel");

  const displayLocale = useMemo(() => {
    if (!locale) return FALLBACK_LOCALE;
    return locale.split("-")[0] || FALLBACK_LOCALE;
  }, [locale]);

  const countries = useMemo(() => buildCountryOptions(displayLocale), [displayLocale]);
  const selectedCountry = useMemo(
    () => countries.find((item) => item.code === country) || countries[0],
    [countries, country]
  );

  const filteredCountries = useMemo(() => filterCountries(countries, searchValue), [countries, searchValue]);

  useEffect(() => {
    if (!open) {
      setSearchValue("");
      return;
    }
    const handleOutsideClick = (event) => {
      if (!dropdownRef.current || dropdownRef.current.contains(event.target)) return;
      setOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      searchRef.current?.focus();
    }
  }, [open]);

  const handleSelect = (nextCountry) => {
    if (disabled) return;
    onCountryChange?.(nextCountry);
    setOpen(false);
  };

  const handleTriggerClick = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const handleSearchClear = () => {
    setSearchValue("");
    searchRef.current?.focus();
  };

  return (
    <div
      className={clsx(styles.countrySelect, compact && styles.countrySelectCompact, className)}
      style={style}
      ref={dropdownRef}
    >
      <button
        type="button"
        className={clsx(styles.countryTrigger, compact && styles.countryTriggerCompact)}
        onClick={handleTriggerClick}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span className={clsx(styles.countryLabel, compact && styles.countryLabelCompact)}>
          {selectedCountry?.code || country} +{selectedCountry?.dialCode}
        </span>
        <span className={styles.dropdownIcon} aria-hidden="true">
          <img src="/images/icons/phoneinput-dropdown.svg" alt="" />
        </span>
      </button>

      {open && (
        <div className={styles.dropdown}>
          <div className={styles.searchRow}>
            <input
              ref={searchRef}
              type="search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder={resolvedSearchPlaceholder}
              aria-label={resolvedSearchPlaceholder}
            />
            {searchValue ? (
              <button type="button" className={styles.clearButton} onClick={handleSearchClear}>
                <img src="/images/icons/phoneinput-close.svg" alt="" />
              </button>
            ) : (
              <span className={styles.searchIcon} aria-hidden="true">
                <img src="/images/icons/phoneinput-search.svg" alt="" />
              </span>
            )}
          </div>
          <div className={styles.divider} />
          {filteredCountries.length ? (
            <ul className={styles.options} role="listbox">
              {filteredCountries.map((item) => (
                <li key={item.code}>
                  <button
                    type="button"
                    className={clsx(styles.option, item.code === selectedCountry?.code && styles.optionActive)}
                    onClick={() => handleSelect(item.code)}
                  >
                    <span className={styles.optionIso}>{item.code}</span>
                    <span className={styles.optionDial}>+{item.dialCode}</span>
                    <span className={styles.optionName}>{item.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className={styles.emptyState}>
              <img src="/images/icons/phoneinput-search-bold.svg" alt="" />
              <span className={styles.emptyText}>{resolvedEmptyLabel}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
